import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { wsService } from '@/services/websocket';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { Topic } from '@/services/api';
import { AdBanner, AD_SLOTS } from '@/modules/ads';
import { adBlockDetector } from '@/modules/ads/services/adBlockDetector';

type MatchingPhase = 
  | 'checking_ad'     // 광고 차단 확인 중
  | 'ad_blocked'      // 광고 차단 감지됨
  | 'connecting'      // WebSocket 연결 중
  | 'in_queue'        // 대기열에서 대기 중
  | 'matched_debater' // 토론자로 매칭됨
  | 'matched_jury'    // 배심원으로 매칭됨
  | 'game_in_progress'// 게임 진행 중 (관전 가능)
  | 'error';

interface MatchingState {
  stances: Record<string, 'pro' | 'con'>;
  topics: Topic[];
}

export default function MatchingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const state = location.state as MatchingState | null;

  const [phase, setPhase] = useState<MatchingPhase>('checking_ad');
  const [queueInfo, setQueueInfo] = useState({
    position: 0,
    totalInQueue: 0,
    matchDeadline: null as number | null,
  });
  const [matchedData, setMatchedData] = useState<{
    debateId: string;
    topicId: string;
    stance?: 'pro' | 'con';
    juryWaitDeadline?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeUntilMatch, setTimeUntilMatch] = useState(0);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // 화면 꺼짐 방지 (Wake Lock API)
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Wake Lock activated');
        }
      } catch (err) {
        console.log('Wake Lock not supported or failed:', err);
      }
    };

    requestWakeLock();

    // 페이지 visibility 변경 시 재요청
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('Wake Lock released');
      }
    };
  }, []);

  // 유효하지 않은 접근 처리
  useEffect(() => {
    if (!state || Object.keys(state.stances).length === 0) {
      navigate('/lobby', { replace: true });
    }
  }, [state, navigate]);

  // 광고 차단 확인 후 대기열 참가
  useEffect(() => {
    if (!state || !user) return;

    let isActive = true;

    async function checkAdBlockAndJoinQueue() {
      // 1. 광고 차단 확인
      const isBlocked = await adBlockDetector.isBlocked();
      
      if (!isActive) return;
      
      if (isBlocked) {
        setPhase('ad_blocked');
        return;
      }

      // 2. 광고 차단 없으면 WebSocket 연결 및 대기열 참가
      setPhase('connecting');
      
      try {
        await wsService.connect();
        
        if (!isActive) return;

        // 대기열 참가 요청
        wsService.send({
          action: 'JOIN_QUEUE',
          userId: user!.userId,
          stances: state!.stances,
        });
      } catch (err) {
        console.error('Queue connection error:', err);
        if (isActive) {
          setError('서버 연결에 실패했습니다');
          setPhase('error');
        }
      }
    }

    checkAdBlockAndJoinQueue();

    return () => {
      isActive = false;
      // 페이지 떠날 때 대기열에서 제거
      if (phase !== 'ad_blocked' && phase !== 'checking_ad') {
        wsService.send({ action: 'LEAVE_QUEUE' });
      }
    };
  }, [state, user]);

  // 광고 차단 해제 후 재시도
  const handleRetryAdBlock = useCallback(async () => {
    setPhase('checking_ad');
    adBlockDetector.reset();
    
    const isBlocked = await adBlockDetector.isBlocked();
    
    if (isBlocked) {
      setPhase('ad_blocked');
    } else {
      setPhase('connecting');
      try {
        await wsService.connect();
        wsService.send({
          action: 'JOIN_QUEUE',
          userId: user!.userId,
          stances: state!.stances,
        });
      } catch (err) {
        setError('서버 연결에 실패했습니다');
        setPhase('error');
      }
    }
  }, [user, state]);

  // WebSocket 메시지 핸들러
  useEffect(() => {
    // 대기열 참가 완료
    const unsubQueueJoined = wsService.on('QUEUE_JOINED', (data) => {
      console.log('Queue joined:', data);
      setPhase('in_queue');
      setQueueInfo({
        position: data.position,
        totalInQueue: data.totalInQueue,
        matchDeadline: data.matchDeadline,
      });
    });

    // 대기열 업데이트
    const unsubQueueUpdate = wsService.on('QUEUE_UPDATE', (data) => {
      setQueueInfo((prev) => ({
        ...prev,
        totalInQueue: data.totalInQueue,
        matchDeadline: data.matchDeadline || prev.matchDeadline,
      }));
    });

    // 진행 중인 게임 알림
    const unsubGameInProgress = wsService.on('GAME_IN_PROGRESS', (data) => {
      console.log('Game in progress:', data);
      setPhase('game_in_progress');
      setMatchedData({
        debateId: data.currentDebateId,
        topicId: '',
      });
    });

    // 토론자로 매칭됨
    const unsubMatchedDebater = wsService.on('MATCHED_AS_DEBATER', (data) => {
      console.log('Matched as debater:', data);
      setPhase('matched_debater');
      setMatchedData({
        debateId: data.debateId,
        topicId: data.topicId,
        stance: data.stance,
        juryWaitDeadline: data.juryWaitDeadline,
      });

      // 바로 토론방으로 이동
      setTimeout(() => {
        navigate(`/room`, {
          replace: true,
          state: {
            role: 'debater',
            topicId: data.topicId,
            stance: data.stance,
            topic: state?.topics.find(t => t.topicId === data.topicId),
          },
        });
      }, 1500);
    });

    // 배심원으로 매칭됨
    const unsubMatchedJury = wsService.on('MATCHED_AS_JURY', (data) => {
      console.log('Matched as jury:', data);
      setPhase('matched_jury');
      setMatchedData({
        debateId: data.debateId,
        topicId: data.topicId,
        juryWaitDeadline: data.juryWaitDeadline,
      });

      // 바로 토론방으로 이동
      setTimeout(() => {
        navigate(`/room`, {
          replace: true,
          state: {
            role: 'jury',
            topicId: data.topicId,
            topic: state?.topics.find(t => t.topicId === data.topicId),
          },
        });
      }, 1500);
    });

    // 에러
    const unsubError = wsService.on('ERROR', (data) => {
      console.error('Queue error:', data);
      setError(data.message);
      setPhase('error');
    });

    return () => {
      unsubQueueJoined();
      unsubQueueUpdate();
      unsubGameInProgress();
      unsubMatchedDebater();
      unsubMatchedJury();
      unsubError();
    };
  }, [navigate, state]);

  // 매칭 타이머 업데이트
  useEffect(() => {
    if (phase !== 'in_queue' || !queueInfo.matchDeadline) return;

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((queueInfo.matchDeadline! - Date.now()) / 1000));
      setTimeUntilMatch(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [phase, queueInfo.matchDeadline]);

  const handleCancel = useCallback(() => {
    console.log('Cancel button clicked');
    wsService.send({ action: 'LEAVE_QUEUE' });
    navigate('/lobby', { replace: true });
  }, [navigate]);

  const handleSpectate = useCallback(() => {
    if (matchedData?.debateId) {
      navigate(`/room`, {
        replace: true,
        state: { role: 'spectator' },
      });
    }
  }, [navigate, matchedData]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!state) return null;

  const selectedCount = Object.values(state.stances).filter(Boolean).length;
  const matchedTopic = matchedData?.topicId 
    ? state.topics.find(t => t.topicId === matchedData.topicId)
    : null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* 광고 차단 확인 중 */}
      {phase === 'checking_ad' && (
        <>
          <div className="w-16 h-16 border-4 border-slate-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 mt-6">준비 중...</p>
        </>
      )}

      {/* 광고 차단 감지됨 */}
      {phase === 'ad_blocked' && (
        <div className="w-full max-w-md text-center">
          <div className="bg-red-900/30 rounded-2xl p-8 border border-red-500/30">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-bold text-white mb-3">
              광고 차단이 감지되었습니다
            </h2>
            <p className="text-slate-300 mb-6 leading-relaxed">
              이 서비스는 광고 수익으로 운영됩니다.<br />
              게임에 참가하려면 광고 차단기를 비활성화해주세요.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleRetryAdBlock}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white font-medium transition-colors"
              >
                광고 차단 해제 후 다시 확인
              </button>
              <button
                onClick={() => navigate('/lobby', { replace: true })}
                className="w-full py-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-slate-300 font-medium transition-colors"
              >
                로비로 돌아가기
              </button>
            </div>

            <div className="mt-6 p-4 bg-slate-800/50 rounded-lg text-sm text-slate-400">
              <p className="font-medium text-slate-300 mb-2">💡 광고 차단 해제 방법</p>
              <ul className="text-left space-y-1">
                <li>• Chrome: 확장 프로그램에서 비활성화</li>
                <li>• Safari: 콘텐츠 차단기 설정에서 해제</li>
                <li>• 브라우저 설정에서 이 사이트 예외 추가</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 연결 중 */}
      {phase === 'connecting' && (
        <>
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 mt-6">서버에 연결하는 중...</p>
        </>
      )}

      {/* 대기열에서 대기 중 */}
      {phase === 'in_queue' && (
        <>
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-primary-500/30 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-4xl">⏳</span>
            </div>
            <div className="absolute inset-0 rounded-full border-4 border-t-primary-500 animate-spin" 
                 style={{ animationDuration: '2s' }} />
          </div>

          <h2 className="text-xl font-semibold mt-8">대기열에서 기다리는 중</h2>
          <p className="text-slate-400 mt-2">
            {selectedCount}개 주제 선택 • 현재 {queueInfo.totalInQueue}명 대기 중
          </p>

          {queueInfo.totalInQueue >= 2 && timeUntilMatch > 0 && (
            <div className="mt-4 text-center">
              <p className="text-sm text-slate-400">매칭까지</p>
              <div className="text-3xl font-mono text-primary-400">
                {formatTime(timeUntilMatch)}
              </div>
            </div>
          )}

          {queueInfo.totalInQueue < 2 && (
            <p className="mt-4 text-sm text-yellow-400">
              매칭을 위해 최소 2명이 필요합니다
            </p>
          )}

          <div className="mt-6 p-4 bg-slate-800/50 rounded-lg text-sm text-slate-400 max-w-xs text-center">
            <p>💡 30초 후 자동으로 토론자/배심원이 배정됩니다</p>
            <p className="mt-1">배심원 참여가 많을수록 토론자 우선권!</p>
          </div>

          <button
            onClick={handleCancel}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleCancel();
            }}
            className="btn-secondary mt-8 px-8 py-4 text-lg min-w-[200px] touch-manipulation"
          >
            취소
          </button>
        </>
      )}

      {/* 토론자로 매칭됨 */}
      {phase === 'matched_debater' && matchedData && (
        <>
          <div className="text-6xl animate-bounce">🎤</div>

          <h2 className="text-2xl font-bold mt-6 text-primary-400">
            토론자로 선정!
          </h2>

          {matchedTopic && (
            <div className="mt-4 text-center">
              <span className="text-3xl">{matchedTopic.emoji}</span>
              <p className="text-sm text-slate-400 mt-2 max-w-xs">
                {matchedTopic.title}
              </p>
            </div>
          )}

          <div className="mt-4 px-4 py-2 rounded-full"
               style={{ 
                 backgroundColor: matchedData.stance === 'pro' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                 border: `1px solid ${matchedData.stance === 'pro' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'}` 
               }}>
            <span className="text-sm">
              {matchedData.stance === 'pro' ? '👍 찬성' : '👎 반대'} 측으로 토론
            </span>
          </div>

          <p className="text-slate-400 mt-4 text-sm">토론방으로 이동합니다...</p>
        </>
      )}

      {/* 배심원으로 매칭됨 */}
      {phase === 'matched_jury' && matchedData && (
        <>
          <div className="text-6xl animate-bounce">👨‍⚖️</div>

          <h2 className="text-2xl font-bold mt-6 text-primary-400">
            배심원으로 참가!
          </h2>

          {matchedTopic && (
            <div className="mt-4 text-center">
              <span className="text-3xl">{matchedTopic.emoji}</span>
              <p className="text-sm text-slate-400 mt-2 max-w-xs">
                {matchedTopic.title}
              </p>
            </div>
          )}

          <div className="mt-4 px-4 py-2 bg-slate-700 rounded-full">
            <span className="text-sm">토론을 듣고 공정한 투표를!</span>
          </div>

          <p className="text-slate-400 mt-4 text-sm">토론방으로 이동합니다...</p>
        </>
      )}

      {/* 게임 진행 중 */}
      {phase === 'game_in_progress' && (
        <>
          <div className="text-6xl">🎙️</div>

          <h2 className="text-xl font-semibold mt-6">토론이 진행 중이에요</h2>
          <p className="text-slate-400 mt-2 text-center">
            현재 토론이 진행 중입니다.<br />
            관전하거나 다음 게임을 기다려주세요.
          </p>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSpectate}
              className="btn-primary"
            >
              👁️ 관전하기
            </button>
            <button
              onClick={() => setPhase('in_queue')}
              className="btn-secondary"
            >
              대기열에서 기다리기
            </button>
          </div>
        </>
      )}

      {/* 에러 */}
      {phase === 'error' && (
        <>
          <div className="text-6xl">😢</div>

          <h2 className="text-xl font-semibold mt-6">오류 발생</h2>
          <p className="text-slate-400 mt-2">{error || '알 수 없는 오류가 발생했습니다'}</p>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => {
                setPhase('connecting');
                setError(null);
              }}
              className="btn-primary"
            >
              다시 시도
            </button>
            <button
              onClick={() => navigate('/lobby')}
              className="btn-secondary"
            >
              로비로
            </button>
          </div>
        </>
      )}

      {/* 광고 배너 - 광고 차단/확인 중일 때는 숨김 */}
      {phase !== 'ad_blocked' && phase !== 'checking_ad' && (
        <div className="absolute bottom-8 w-full max-w-sm px-4">
          <AdBanner 
            adSlot={AD_SLOTS.MATCHING_BANNER} 
            adFormat="horizontal"
            className="h-16"
          />
        </div>
      )}
    </div>
  );
}
